import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { holidayService } from '../../services/holidayService';
import {
    buildFixedHolidayDates,
    buildFixedHolidaysForYear,
} from '../../utils/brazilianHolidays';
import type { Holiday, HolidayEntry } from '../../models/holiday.types';
import { projectRecurringDates } from '../../../../../shared/vacations/vacationHolidayDates';

export const HOLIDAYS_QUERY_KEY = 'holidays';

const CALENDAR_YEARS_BACK = 1;
const CALENDAR_YEARS_AHEAD = 2;

function toEntry(holiday: Holiday): HolidayEntry {
    return {
        id: holiday.id,
        date: holiday.date,
        name: holiday.name,
        scope: holiday.scope,
        recurring: holiday.recurring,
        source: 'custom',
        locked: false,
    };
}

export function mergeHolidayEntries(fixed: HolidayEntry[], custom: Holiday[]): HolidayEntry[] {
    const takenDates = new Set(fixed.map((holiday) => holiday.date));

    const merged = [
        ...fixed,
        ...custom.filter((holiday) => !takenDates.has(holiday.date)).map(toEntry),
    ];

    return merged.sort((a, b) => a.date.localeCompare(b.date));
}

export function useHolidaysQuery(year: number) {
    const queryResult = useQuery({
        queryKey: [HOLIDAYS_QUERY_KEY, year],
        queryFn: async (): Promise<Holiday[]> => holidayService.list(year),
        staleTime: 30 * 60 * 1000,
    });

    const holidays = useMemo(
        () => mergeHolidayEntries(buildFixedHolidaysForYear(year), queryResult.data ?? []),
        [year, queryResult.data]
    );

    return {
        holidays,
        customHolidays: queryResult.data ?? [],
        isLoading: queryResult.isLoading,
        error: queryResult.error,
    };
}

export function useHolidayDatesQuery() {
    const queryResult = useQuery({
        queryKey: [HOLIDAYS_QUERY_KEY, 'all'],
        queryFn: async (): Promise<Holiday[]> => holidayService.list(),
        staleTime: 30 * 60 * 1000,
    });

    const holidayDates = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const fixed = buildFixedHolidayDates(
            currentYear - CALENDAR_YEARS_BACK,
            currentYear + CALENDAR_YEARS_AHEAD
        );
        const years = Array.from(
            { length: CALENDAR_YEARS_BACK + CALENDAR_YEARS_AHEAD + 1 },
            (_, index) => currentYear - CALENDAR_YEARS_BACK + index
        );
        const custom = projectRecurringDates(queryResult.data ?? [], years);

        return Array.from(new Set([...fixed, ...custom]));
    }, [queryResult.data]);

    return { holidayDates, isLoading: queryResult.isLoading, error: queryResult.error };
}
